import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faEdit, faSave, faUsers } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { updateGateway, deleteGateway } from "../redux/slices/gatewaysSlice";
import { deleteGatewayById, updateGatewayById } from "../services/gatewayService";
import gatewayIcon from "../assets/Gateway.PNG";
import { useNotification } from "../notifications/Notifications";
import { getTranslation } from "../translations/getTranslations";
import RecipientManagerModal from "../components/modals/RecipientManagerModal";
import { setSelectedGateway } from "../redux/slices/gatewaysSlice";
import ConfirmModal from "./modals/ConfirmModal";

const Gateway = ({ isMobile, gateway, index, type }) => {
  const [editIndex, setEditIndex] = useState(null);
  const [temporaryName, setTemporaryName] = useState("");
  const [notificationsEnabled, setNotificationsEnabled] = useState({});
  const [recipientModalOpen, setRecipientModalOpen] = useState(false);
  const [currentGatewayId, setCurrentGatewayId] = useState(null);

  const dispatch = useDispatch();
  const notify = useNotification();

  const language = useSelector(state => state.settings.language);
  const gateways = useSelector(state => state.gateways.data);
  const translation = key => getTranslation("Gateway", key, language);

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const handleDeleteGateway = async () => {
    try {
      await deleteGatewayById(gateway.id);
      dispatch(deleteGateway(gateway.id));
      notify("success", translation("_deleteGatewaySuccess"));
    } catch (error) {
      notify("error", translation("_deleteGatewayError"));
    }
  };

  const handleEditClick = () => {
    setEditIndex(index);
    setTemporaryName(gateways[index].name);
    setNotificationsEnabled(prevState => ({
      ...prevState,
      [index]: gateways[index].notificationsEnabled || false,
    }));
  };

  const handleSave = async () => {
    const updatedNotificationsEnabled = notificationsEnabled[index];

    if (temporaryName !== gateway.name || updatedNotificationsEnabled !== gateway.notificationsEnabled) {
      try {
        const resultKey = await updateGatewayById(gateway.id, temporaryName, updatedNotificationsEnabled);
        dispatch(
          updateGateway({
            id: gateway.id,
            updates: {
              name: temporaryName,
              notificationsEnabled: updatedNotificationsEnabled,
            },
          }),
        );

        if (type === "hosts") {
          const updatedGateway = {
            ...gateway,
            name: temporaryName,
            notificationsEnabled: updatedNotificationsEnabled,
          };
          dispatch(setSelectedGateway(updatedGateway));
        }

        const translationKey = resultKey?.trim().replace(/^"+|"+$/g, "");
        notify("success", translation(translationKey));
      } catch (error) {
        const errorKey = error.message?.trim().replace(/^"+|"+$/g, "");
        notify("error", translation(errorKey));
      }
    }
    setEditIndex(null);
  };

  const handleCancelEdit = () => {
    setTemporaryName(gateway.name);
    setNotificationsEnabled(gateway.notificationsEnabled || false);
    setEditIndex(null);
  };

  const handleNotificationToggle = () => {
    const gateway = gateways[index];

    if (!notificationsEnabled[index] && gateway.recipients.length === 0) {
      notify("error", translation("_noRecipientsAlert"));
      return;
    }

    setNotificationsEnabled(prevState => {
      return {
        ...prevState,
        [index]: !prevState[index],
      };
    });
  };

  const isOwnerGateway = gateway.isOwner === true || gateway.isOwner === 1;

  const handleManageRecipients = gatewayId => {
    setCurrentGatewayId(gatewayId);
    setRecipientModalOpen(true);
  };

  const determineTemperatureColor = temperature => {
    if (temperature <= 60) {
      return "text-green-500";
    } else if (temperature <= 80) {
      return "text-orange-400";
    } else {
      return "text-red-500";
    }
  };

  return (
    <div
      key={index}
      className={`flex h-auto flex-col items-center overflow-hidden rounded border bg-inputBackground text-textPrimary shadow-md transition-shadow hover:shadow-lg ${
        type === "manageGateways" ? "sm:w-[80%] lg:w-[65%] xl:w-[40%] 2xl:w-[30%]" : "w-[100%]"
      } `}
    >
      {type === "manageGateways" && (
        <div
          className={`h-20 w-full`}
          style={{
            backgroundImage: `url(${gatewayIcon})`,
            backgroundSize: "contain",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        ></div>
      )}
      <div className="flex w-full flex-col justify-between p-4">
        <div className={`mb-4 flex flex-col rounded-lg border border-inputBorder p-4 shadow-md`}>
          <div className={`flex w-full justify-end`}>
            <div
              className={`h-4 w-4 rounded-full ${
                gateway.gatewayStatus === true ? "animate-pulse bg-successStatus" : "bg-neutralStatus"
              }`}
            ></div>
          </div>
          <div className={`flex flex-col space-y-4`}>
            {/* Details Section */}
            <div className="flex flex-col gap-2 text-textPrimary">
              <div className="flex font-bold underline">{translation("_details")}</div>
              <div
                className={`flex ${isMobile ? "flex-col items-center gap-1" : "flex-row items-center justify-between"} p-1`}
              >
                <strong>{translation("_gatewayName")}:</strong>
                <input
                  className={`min-w-0 flex-1 rounded transition duration-300 ease-in-out focus:outline-none ${isMobile ? "self-center px-1 text-center" : "px-2 text-end text-lg"} ${
                    editIndex === index
                      ? "shadow-buttonCancel/30 bg-backgroundPrimary shadow-inner ring-2 ring-buttonCancel"
                      : "border border-inputBackground bg-inputBackground"
                  }`}
                  type="text"
                  value={editIndex === index ? temporaryName : gateway.name}
                  disabled={editIndex !== index}
                  onChange={e => setTemporaryName(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleSave()}
                />
              </div>
              <div
                className={`flex ${isMobile ? "flex-col items-center gap-1" : "flex-row items-center justify-between"} p-1`}
              >
                <strong>{translation("_serialNumber")}:</strong>
                <span className="px-2">{gateway.olySerialNumber}</span>
              </div>
              <div
                className={`flex ${isMobile ? "flex-col items-center gap-1" : "flex-row items-center justify-between"} p-1`}
              >
                <strong>{translation("_localAddress")}:</strong>
                <span className="px-2">{gateway.localAddress}</span>
              </div>
              <div
                className={`flex ${isMobile ? "flex-col items-center gap-1" : "flex-row items-center justify-between"} p-1`}
              >
                <strong>{translation("_hardwareSerial")}:</strong>
                <span className="px-2">{gateway.serialNumber}</span>
              </div>
              <div
                className={`flex ${isMobile ? "flex-col items-center gap-1" : "flex-row items-center justify-between"} p-1`}
              >
                <strong>{translation("_apiVersion")}:</strong>
                <span className="px-2">{gateway.apiVersion}</span>
              </div>
              <div
                className={`flex ${isMobile ? "flex-col items-center gap-1" : "flex-row items-center justify-between"} p-1`}
              >
                <strong>{translation("_temperature")}:</strong>
                <span className={`${determineTemperatureColor(parseFloat(gateway.temperature))} px-2 font-semibold`}>
                  {gateway.temperature} °C
                </span>
              </div>
            </div>

            {/* Notifications Section */}
            <div className="flex flex-col gap-2">
              <div className={`flex ${isMobile ? "justify-center" : "justify-start"} w-full font-bold underline`}>
                {translation("_notifications")}
              </div>

              <div
                className={`flex items-center justify-between space-x-2 rounded-lg p-1 transition duration-300 ease-in-out ${
                  editIndex === index
                    ? "shadow-buttonCancel/30 bg-backgroundPrimary shadow-inner ring-2 ring-buttonCancel"
                    : "border border-inputBackground bg-inputBackground"
                }`}
              >
                <div className="flex w-[70%] flex-col">
                  <strong className="text-textPrimary">
                    {editIndex === index
                      ? translation("_notificationsEdit")
                      : gateway.notificationsEnabled && (!isOwnerGateway || gateway.recipients.length !== 0)
                        ? translation("_notificationsEnabled")
                        : translation("_notificationsDisabled")}
                  </strong>

                  {gateway.notificationsEnabled && isOwnerGateway && gateway.recipients.length === 0 && (
                    <span className="text-warning mt-1 text-xs">{translation("_notificationsMissingRecipients")}</span>
                  )}
                </div>

                <label
                  className="relative inline-block h-[25px] w-12"
                  title={editIndex !== index ? translation("_editRequiredNotice") : ""}
                >
                  <input
                    type="checkbox"
                    className="peer h-0 w-0 opacity-0"
                    checked={
                      (editIndex === index ? notificationsEnabled[index] : gateway.notificationsEnabled) &&
                      (!isOwnerGateway || gateway.recipients.length !== 0)
                    }
                    onChange={handleNotificationToggle}
                    disabled={editIndex !== index}
                  />
                  <span className="slider absolute inset-0 rounded-full bg-gray-300 peer-checked:bg-inputSliderChecked"></span>
                </label>
              </div>

              <div
                className={`${gateway.isOwner === true ? "flex" : "hidden"} ${isMobile ? "flex-col items-center gap-1" : "flex-row items-center justify-between"} p-1`}
              >
                <strong className="text-textPrimary">{translation("_recipients")}:</strong>
                <button
                  className="flex items-center justify-center rounded bg-buttonSubmit px-2 py-1 text-buttonText hover:bg-buttonSubmitHover"
                  onClick={() => handleManageRecipients(gateway.id)}
                >
                  <FontAwesomeIcon icon={faUsers} className="mr-2" />
                  {translation("_manageRecipients")}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className={`justify-end gap-4 ${gateway.isOwner === true ? "flex" : "hidden"}`}>
          {editIndex === index ? (
            <>
              <button
                className={`flex items-center justify-center rounded bg-buttonSubmit px-2 py-1 text-buttonText hover:bg-buttonSubmitHover`}
                onClick={handleSave}
              >
                <FontAwesomeIcon icon={faSave} className="mr-2" />
                {translation("_save")}
              </button>
              <button
                className={`flex items-center justify-center rounded bg-buttonCancel px-2 py-1 text-buttonText hover:bg-buttonCancelHover`}
                onClick={handleCancelEdit}
              >
                {translation("_cancel")}
              </button>
            </>
          ) : (
            <>
              <button
                className={`flex items-center justify-center rounded bg-buttonSubmit p-2 text-buttonText hover:bg-buttonSubmitHover`}
                onClick={handleEditClick}
              >
                <FontAwesomeIcon icon={faEdit} />
              </button>
              <button
                className={`flex items-center justify-center rounded bg-buttonCancel p-2 text-buttonText hover:bg-buttonCancelHover`}
                onClick={() => setConfirmModalOpen(true)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </>
          )}
        </div>
      </div>
      <RecipientManagerModal
        gatewayId={currentGatewayId}
        showModal={recipientModalOpen}
        onClose={() => setRecipientModalOpen(false)}
      />
      <ConfirmModal
        showModal={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleDeleteGateway}
        titleKey="_confirmTitle"
        messageKey="_deleteGatewayConfirm"
      />
    </div>
  );
};

export default Gateway;
